/**
 * Shared crop-circle dropdown styling and list item for alert feature.
 * Use the same look and behavior as alertLogicPicker dropdowns across alerts.
 */
import { Pressable, StyleSheet, Text } from 'react-native';
import {
  RenderListItemPropsInterface,
  ValueType,
} from 'react-native-dropdown-picker';
import { RadioButton } from 'react-native-paper';
import { Icon } from '../../crop-circle/icons';
import { tokens } from '../../crop-circle/tokens';

const DROPDOWN_MAX_HEIGHT = 700;

export const cropCircleDropdownStyles = StyleSheet.create({
  dropdown: {
    minHeight: 52,
    backgroundColor: tokens.colors.neutral[200],
    borderRadius: 4,
    borderWidth: 0,
    paddingHorizontal: tokens.spacing.lg,
  },
  placeholderLabel: {
    ...tokens.typography.body.medium,
    color: tokens.colors.neutral[400],
  },
  itemLabel: {
    ...tokens.typography.body.medium,
    color: tokens.colors.brand.black,
  },
  dropdownListContainer: {
    flexGrow: 1,
    marginTop: tokens.spacing.sm,
    paddingVertical: tokens.spacing.md,
    paddingHorizontal: 0,
    position: 'relative' as const,
    top: 0,
    zIndex: 1000,
    elevation: 5,
    borderWidth: 0,
    backgroundColor: tokens.colors.neutral[100],
  },
  listItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 52,
    paddingHorizontal: tokens.spacing.lg,
    backgroundColor: tokens.colors.neutral[200],
    marginBottom: tokens.spacing.sm,
    borderRadius: 4,
    borderWidth: 0,
  },
});

const chevronIcon = () => (
  <Icon name="Chevron Down" size={20} color={tokens.colors.brand.black} />
);

/** Default list item: label + radio (black when selected), same as alertLogicPicker */
export function CropCircleDropdownListItem<T extends ValueType>({
  label,
  isSelected,
  onPress,
}: RenderListItemPropsInterface<T>) {
  const radioButtonValue = (isSelected && 'checked') || 'unchecked';
  return (
    <Pressable onPress={onPress} style={cropCircleDropdownStyles.listItem}>
      <Text style={cropCircleDropdownStyles.itemLabel}>{label}</Text>
      <RadioButton.Android
        value="test"
        status={radioButtonValue}
        uncheckedColor={tokens.colors.neutral[400]}
        color={tokens.colors.brand.black}
        hitSlop={10}
        onPress={onPress}
      />
    </Pressable>
  );
}

export type CropCircleDropDownPickerOptions = {
  maxHeight?: number;
  openDropdownOnLoad?: boolean;
  style?: object | object[];
  placeholderStyle?: object;
  textStyle?: object;
  dropDownContainerStyle?: object | object[];
  listItemContainerStyle?: object;
  renderListItem?: (props: RenderListItemPropsInterface<any>) => JSX.Element;
  ArrowDownIconComponent?: () => JSX.Element;
  ArrowUpIconComponent?: () => JSX.Element;
  [key: string]: any;
};

/**
 * Returns dropDownPickerProps that match alertLogicPicker crop-circle dropdown.
 * Use with ControlledSelect: dropDownPickerProps={getCropCircleDropDownPickerProps({ ... })}
 */
export function getCropCircleDropDownPickerProps(
  options: CropCircleDropDownPickerOptions = {}
): Record<string, unknown> {
  const {
    maxHeight = DROPDOWN_MAX_HEIGHT,
    openDropdownOnLoad,
    style,
    placeholderStyle,
    textStyle,
    dropDownContainerStyle,
    listItemContainerStyle,
    renderListItem = (props: RenderListItemPropsInterface<any>) => (
      <CropCircleDropdownListItem {...props} />
    ),
    ArrowDownIconComponent = chevronIcon,
    ArrowUpIconComponent = chevronIcon,
    ...rest
  } = options;

  return {
    style: style ?? cropCircleDropdownStyles.dropdown,
    textStyle: textStyle ?? cropCircleDropdownStyles.itemLabel,
    placeholderStyle: placeholderStyle ?? cropCircleDropdownStyles.placeholderLabel,
    dropDownContainerStyle: dropDownContainerStyle ?? [
      cropCircleDropdownStyles.dropdownListContainer,
      { minHeight: 'auto' },
      { maxHeight },
    ],
    listItemContainerStyle: listItemContainerStyle ?? undefined,
    scrollViewProps: {
      nestedScrollEnabled: true,
      style: { backgroundColor: tokens.colors.neutral[100] },
      contentContainerStyle: {
        backgroundColor: tokens.colors.neutral[100],
      },
    },
    openDropdownOnLoad,
    ArrowDownIconComponent,
    ArrowUpIconComponent,
    renderListItem,
    ...rest,
  };
}

export { DROPDOWN_MAX_HEIGHT };
